import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    alpha,
    useTheme,
    Tabs,
    Tab,
    IconButton,
    Chip,
    Avatar,
    Tooltip,
    Skeleton,
    Grid,
    Stack,
    Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, FormControl, InputLabel, Select,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    AccountBalance as FinancialIcon,
    Receipt as InvoicesIcon,
    PriceChange as PricingIcon,
    Rule as SurchargeIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    MoreVert as MoreIcon,
    TrendingUp as RevenueIcon,
    Payment as PaidIcon,
    ErrorOutline as PendingIcon,
    Analytics as HealthIcon,
    Download as DownloadIcon,
    Email as EmailIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PlayArrow as TestIcon,
    ViewList as ViewListIcon,
    ViewKanban as ViewKanbanIcon
} from '@mui/icons-material';
import KanbanView from '../../components/ui/KanbanView';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomers } from '../../hooks/useCustomers';
import PageWrapper from '../../components/common/PageWrapper';
import StatsCard, { StatsCardGrid } from '../../components/common/StatsCard';
import SmartDataGrid from '../../components/common/SmartDataGrid';
import GlassPanel from '../../components/common/GlassPanel';
import {
    useInvoices,
    useBillingStats,
    useSurchargeRules,
    useOperationCosts,
    useClientRateCards,
    useCreateClientRateCard,
    useCreateInvoice,
    useDownloadInvoicePDF,
    useSendInvoiceEmail,
    useUpdateInvoice
} from '../../hooks/useBilling';
import PricingGroupsPanel from '../../components/billing/PricingGroupsPanel';
import InvoiceItemsPanel from '../../components/billing/InvoiceItemsPanel';
import InvoiceTemplatesPanel from '../../components/billing/InvoiceTemplatesPanel';
import InvoiceHistoryPanel from '../../components/billing/InvoiceHistoryPanel';
import InvoiceWorkflowPanel from '../../components/billing/InvoiceWorkflowPanel';
import CustomerManagementPanel from '../../components/billing/CustomerManagementPanel';
import InvoicePDFGenerator from '../../components/billing/InvoicePDFGenerator';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';

/**
 * Kanban columns for invoice status workflow
 */
const getInvoiceStatusKanbanColumns = (t) => [
    { id: 'draft', label: t('billing.status.draft', 'Draft'), color: '#9E9E9E' },
    { id: 'sent', label: t('billing.status.sent', 'Sent'), color: '#2196F3' },
    { id: 'paid', label: t('billing.status.paid', 'Paid'), color: '#4CAF50' },
    { id: 'overdue', label: t('billing.status.overdue', 'Overdue'), color: '#f44336' }
];

/**
 * FinancialCockpitPage - High-fidelity Financial & Billing Orchestration Center
 * Aurora 2026 Design Standard
 */
const FinancialCockpitPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    // Tab Management (handle query param tab=N)
    const queryParams = new URLSearchParams(location.search);
    const initialTab = parseInt(queryParams.get('tab') || '0', 10);
        const [activeTab, setActiveTab] = useState(initialTab);
        const [selectedItem, setSelectedItem] = useState(null);
        const [isSidebarOpen, setIsSidebarOpen] = useState(false);
        const [invoicesViewMode, setInvoicesViewMode] = useState('table');

    // Data Fetching
    const { data: invoicesData, isLoading: invoicesLoading, refetch: refetchInvoices } = useInvoices();
    const { data: statsData, isLoading: statsLoading } = useBillingStats();
    const { data: rulesData, isLoading: rulesLoading, refetch: refetchRules } = useSurchargeRules();

    const { data: customersData } = useCustomers();
    const { data: rateCardsData, isLoading: rateCardsLoading } = useClientRateCards();
    const createInvoiceMutation = useCreateInvoice();
    const createRateCardMutation = useCreateClientRateCard();
    const downloadPDFMutation = useDownloadInvoicePDF();
    const sendEmailMutation = useSendInvoiceEmail();
    const updateInvoiceMutation = useUpdateInvoice();

        // Invoice Logic
        const [invoiceOpen, setInvoiceOpen] = useState(false);
        const [emailDialogOpen, setEmailDialogOpen] = useState(false);
        const [pdfGeneratorOpen, setPdfGeneratorOpen] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState('');
    const [invoiceForm, setInvoiceForm] = useState({
        customer_id: '',
        due_date: new Date().toISOString().split('T')[0],
        items: [] // Simplified for now
    });

    const handleCreateInvoice = async () => {
        try {
            await createInvoiceMutation.mutateAsync({
                ...invoiceForm,
                status: 'draft',
                total_amount: 0
            });
            setInvoiceOpen(false);
            refetchInvoices();
        } catch (err) {
            console.error("Failed to create invoice", err);
        }
    };

    const handleDownloadPDF = useCallback(async (invoiceId) => {
        try {
            const blob = await downloadPDFMutation.mutateAsync(invoiceId);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice-${invoiceId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download PDF', err);
        }
    }, [downloadPDFMutation]);

    const handleSendEmail = useCallback(async () => {
        if (!selectedItem || !emailRecipients.trim()) return;
        try {
            const recipients = emailRecipients.split(',').map(e => e.trim()).filter(Boolean);
            await sendEmailMutation.mutateAsync({ invoiceId: selectedItem.id, recipients });
            setEmailDialogOpen(false);
            setEmailRecipients('');
        } catch (err) {
            console.error('Failed to send email', err);
        }
    }, [selectedItem, emailRecipients, sendEmailMutation]);


    const isLoading = invoicesLoading || statsLoading || rulesLoading;

    // Memoized Data
    const invoices = useMemo(() => invoicesData?.items || invoicesData || [], [invoicesData]);
    const rules = useMemo(() => rulesData?.items || rulesData || [], [rulesData]);
    const stats = useMemo(() => statsData || {}, [statsData]);

        // Tab Handlers
        const handleTabChange = (event, newValue) => {
            setActiveTab(newValue);
            setSelectedItem(null);
            setIsSidebarOpen(false);
            navigate(`/admin/financial?tab=${newValue}`, { replace: true });
        };

        const handleInvoicesViewModeChange = useCallback((event, newMode) => {
            if (newMode !== null) {
                setInvoicesViewMode(newMode);
            }
        }, []);

        // Transform invoices data for KanbanView
        const kanbanInvoices = useMemo(() => {
            return invoices.map(invoice => ({
                id: invoice.id,
                title: invoice.invoice_number || `INV-${invoice.id}`,
                subtitle: `${invoice.customer_name || invoice.client_name || 'Unknown'} - ${formatCurrency(invoice.total_amount || 0, invoice.currency || 'EUR')}`,
                status: invoice.status?.toLowerCase() || 'draft',
                metadata: {
                    customer: invoice.customer_name || invoice.client_name,
                    amount: invoice.total_amount,
                    dueDate: invoice.due_date
                }
            }));
        }, [invoices]);


    const rateCardColumns = [
        { id: 'name', label: t('common.financial.rateCardName', 'Rate Card Name') },
        { id: 'client_name', label: t('common.client', 'Client') },
        { id: 'valid_from', label: t('common.financial.validFrom', 'Valid From') },
        { id: 'valid_to', label: t('common.financial.validTo', 'Valid To') },
        { id: 'status', label: t('common.status', 'Status'), render: (value) => <Chip label={value} size="small" /> }
    ];

    // Invoice Columns
    const invoiceColumns = [
        { id: 'id', label: 'ID' },
        {
            id: 'invoice_number',
            label: t('billing.invoices.number', 'Invoice #'),
            render: (value, row) => (
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {value || `INV-${row.id}`}
                </Typography>
            )
        },
        {
            id: 'customer_name',
            label: t('common.customer', 'Customer'),
            render: (value, row) => (
                <Typography variant="body2">{value || row.client_name || '-'}</Typography>
            )
        },
        {
            id: 'status',
            label: t('common.status', 'Status'),
            render: (value) => {
                const status = value?.toLowerCase() || 'draft';
                const colors = {
                    paid: 'success',
                    sent: 'info',
                    overdue: 'error',
                    draft: 'default'
                };
                return (
                    <Chip
                        label={t(`billing.status.${status}`, status.toUpperCase())}
                        size="small"
                        color={colors[status] || 'default'}
                        variant="outlined"
                    />
                );
            }
        },
        {
            id: 'total_amount',
            label: t('billing.amount', 'Total Amount'),
            render: (value, row) => (
                <Typography variant="body2" fontWeight="medium">
                    {formatCurrency(value || 0, row.currency || 'EUR')}
                </Typography>
            )
        },
        {
            id: 'due_date',
            label: t('billing.dueDate', 'Due Date'),
            render: (value, row) => (
                <Typography variant="body2" color={row.status === 'overdue' ? 'error.main' : 'text.secondary'}>
                    {value ? formatDate(value, 'dd MMM yyyy') : '-'}
                </Typography>
            )
        }
    ];

    // Surcharge Rule Columns
    const ruleColumns = [
        { id: 'id', label: 'ID' },
        { id: 'name', label: t('billing.ruleName', 'Rule Name') },
        {
            id: 'type',
            label: t('common.type', 'Type'),
            render: (value) => (
                <Chip label={value} size="small" variant="contained" sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main }} />
            )
        },
        {
            id: 'is_active',
            label: t('common.active', 'Active'),
            render: (value) => (
                <Chip label={value ? 'ON' : 'OFF'} size="small" color={value ? 'success' : 'default'} />
            )
        },
        { id: 'priority', label: t('common.priority', 'Priority') }
    ];

    return (
        <PageWrapper
            title={t('billing.cockpit.title', 'Financial & Billing Cockpit')}
            subtitle={t('billing.cockpit.subtitle', 'Strategic oversight of revenue lifecycles, pricing models, and surcharge orchestration')}
            breadcrumbs={[
                { label: t('nav.admin', 'Admin'), path: '/admin' },
                { label: t('billing.cockpit.title', 'Financial Cockpit'), icon: FinancialIcon }
            ]}
            actions={
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                            refetchInvoices();
                            refetchRules();
                        }}
                    >
                        {t('common.refresh', 'Refresh')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => activeTab === 0 ? setInvoiceOpen(true) : null}
                    >
                        {activeTab === 0 ? t('billing.createInvoice', 'Generate Invoice') : t('billing.newAction', 'New Strategic Action')}
                    </Button>
                </Box>
            }
        >
            {/* KPI Cards Section */}
            <Box sx={{ mb: 4 }}>
                <StatsCardGrid columns={4}>
                    <StatsCard
                        icon={RevenueIcon}
                        title={t('billing.totalRevenue', 'Total Revenue (MTD)')}
                        value={isLoading ? <Skeleton width={80} /> : formatCurrency(stats.total_revenue || 0, stats.currency || 'EUR')}
                        trend={stats.revenue_trend || t('common.noData', 'No data')}
                        color={theme.palette.primary.main}
                    />
                    <StatsCard
                        icon={PendingIcon}
                        title={t('billing.outstanding', 'Outstanding Invoices')}
                        value={isLoading ? <Skeleton width={40} /> : stats.outstanding_invoices || 0}
                        trendColor="warning"
                        trend={stats.outstanding_trend || t('common.noData', 'No data')}
                        color={theme.palette.warning.main}
                    />
                    <StatsCard
                        icon={PaidIcon}
                        title={t('billing.paid', 'Paid (MTD)')}
                        value={isLoading ? <Skeleton width={60} /> : formatCurrency(stats.paid_amount || 0, stats.currency || 'EUR')}
                        trend={stats.collection_rate ? `${stats.collection_rate}% Collection Rate` : t('common.noData', 'No data')}
                        trendColor="success"
                        color={theme.palette.success.main}
                    />
                    <StatsCard
                        icon={HealthIcon}
                        title={t('billing.healthIndex', 'Financial Health Index')}
                        value={isLoading ? <Skeleton width={60} /> : (stats.health_index || 0).toFixed(1)}
                        subtitle={stats.health_status || t('common.noData', 'No data')}
                        color={theme.palette.info.main}
                    />
                </StatsCardGrid>
            </Box>

            {/* Main Cockpit Interface */}

            <Dialog open={invoiceOpen} onClose={() => setInvoiceOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('common.financial.generateInvoice', 'Generate Commercial Invoice')}</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>{t('common.financial.selectCustomer', 'Select Customer')}</InputLabel>
                            <Select
                                value={invoiceForm.customer_id}
                                label={t('common.financial.selectCustomer', 'Select Customer')}
                                onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_id: e.target.value })}
                            >
                                {customersData?.items?.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField
                            type="date"
                            label={t('common.financial.dueDate', 'Due Date')}
                            InputLabelProps={{ shrink: true }}
                            value={invoiceForm.due_date}
                            onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                        />
                        <Alert severity="info">{t('common.financial.lineItemsAutoCalculated', 'Line items will be auto-calculated from unbilled shipments.')}</Alert>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInvoiceOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                    <Button variant="contained" onClick={handleCreateInvoice}>{t('common.financial.generateDraft', 'Generate Draft')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('common.financial.sendInvoiceEmail', 'Send Invoice Email')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {t('common.financial.emailDescription', 'Enter recipient email addresses separated by commas')}
                    </Typography>
                    <TextField
                        fullWidth
                        label={t('common.financial.emailRecipients', 'Recipients')}
                        value={emailRecipients}
                        onChange={(e) => setEmailRecipients(e.target.value)}
                        placeholder="client@example.com, finance@example.com"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                    <Button variant="contained" onClick={handleSendEmail} disabled={sendEmailMutation.isPending}>
                        {sendEmailMutation.isPending ? t('common.sending', 'Sending...') : t('common.send', 'Send')}
                    </Button>
                </DialogActions>
            </Dialog>

            <GlassPanel sx={{ p: 0, overflow: 'hidden',display: 'flex', flexDirection: 'column', height: 'calc(100vh - 400px)', minHeight: 650 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 64 }}>
                        <Tab
                            icon={<InvoicesIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('billing.tabs.invoices', 'Invoices')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<PricingIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('billing.tabs.pricing', 'Client Pricing')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<SurchargeIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('billing.tabs.surcharges', 'Surcharge Rules')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<HealthIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('billing.tabs.revenue', 'Balance & Revenue')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<PricingIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('business.pricingGroups.title', 'Pricing Groups')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<InvoicesIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('business.invoicing.items.title', 'Invoice Items')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<InvoicesIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('business.invoicing.templates.title', 'Templates')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<HealthIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('business.invoicing.history.title', 'History')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<PricingIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('business.invoicing.workflow.title', 'Workflow')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<FinancialIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('business.customers.title', 'Customers')}
                            sx={{ minHeight: 64 }}
                        />
                    </Tabs>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('common.financial.exportLedger', 'Export Ledger')}>
                            <IconButton size="small"><DownloadIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.financial.filterRegistry', 'Filter Registry')}>
                            <IconButton size="small"><FilterIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.financial.auditSettings', 'Audit Settings')}>
                            <IconButton size="small"><MoreIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                    <Box sx={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        {/* Grid Area */}
                        <Box sx={{
                            flexGrow: 1,
                            height: '100%',
                            width: isSidebarOpen ? 'calc(100% - 420px)' : '100%',
                            transition: 'all 0.3s ease'
                        }}>
                                                        {activeTab === 0 && (
                                                            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                                                                    <ToggleButtonGroup
                                                                        value={invoicesViewMode}
                                                                        exclusive
                                                                        onChange={handleInvoicesViewModeChange}
                                                                        size="small"
                                                                    >
                                                                        <ToggleButton value="table" aria-label="Table view">
                                                                            <Tooltip title={t('common.tableView', 'Table View')}>
                                                                                <ViewListIcon />
                                                                            </Tooltip>
                                                                        </ToggleButton>
                                                                        <ToggleButton value="kanban" aria-label="Kanban view">
                                                                            <Tooltip title={t('common.kanbanView', 'Kanban View')}>
                                                                                <ViewKanbanIcon />
                                                                            </Tooltip>
                                                                        </ToggleButton>
                                                                    </ToggleButtonGroup>
                                                                </Box>
                                                                {invoicesViewMode === 'kanban' ? (
                                                                    <KanbanView
                                                                        columns={getInvoiceStatusKanbanColumns(t)}
                                                                        items={kanbanInvoices}
                                                                        loading={invoicesLoading}
                                                                    />
                                                                ) : (
                                                                    <SmartDataGrid
                                                                        data={invoices}
                                                                        columns={invoiceColumns}
                                                                        loading={invoicesLoading}
                                                                        onRowClick={(row) => {
                                                                            setSelectedItem({ ...row, _type: 'invoice' });
                                                                            setIsSidebarOpen(true);
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        )}
                            {activeTab === 1 && (
                                <SmartDataGrid
                                    data={rateCardsData || []}
                                    columns={rateCardColumns}
                                    loading={rateCardsLoading}
                                />
                            )}
                            {activeTab === 2 && (
                                <SmartDataGrid
                                    data={rules}
                                    columns={ruleColumns}
                                    loading={rulesLoading}
                                    onRowClick={(row) => {
                                        setSelectedItem({ ...row, _type: 'rule' });
                                        setIsSidebarOpen(true);
                                    }}
                                />
                            )}
                            {activeTab === 3 && (
                                <Box sx={{ p: 4, height: '100%' }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={8}>
                                            <Typography variant="h6" gutterBottom>{t('common.financial.revenueSegmentation', 'Revenue Segmentation (Real-time)')}</Typography>
                                            <Box sx={{ height: 350, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 2, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography color="text.secondary">{t('common.financial.visualizationEngine', 'Financial Visualization Engine')}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="h6" gutterBottom>{t('common.financial.criticalAccounts', 'Critical Accounts')}</Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {(stats.critical_accounts || []).length > 0 ? (
                                                    (stats.critical_accounts || []).slice(0, 3).map((account, i) => (
                                                        <Box key={account.id || i} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                <Typography variant="subtitle2">{account.name || account.customer_name || t('common.unknown', 'Unknown')}</Typography>
                                                                <Typography variant="caption" color="error.main">{account.status || t('billing.status.overdue', 'Overdue')}</Typography>
                                                            </Box>
                                                            <Typography variant="h6">{formatCurrency(account.amount || 0, account.currency || 'EUR')}</Typography>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">{t('common.noData', 'No critical accounts')}</Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                            {activeTab === 4 && (
                                <PricingGroupsPanel />
                            )}
                            {activeTab === 5 && (
                                <InvoiceItemsPanel />
                            )}
                            {activeTab === 6 && (
                                <InvoiceTemplatesPanel />
                            )}
                            {activeTab === 7 && (
                                <InvoiceHistoryPanel />
                            )}
                            {activeTab === 8 && (
                                <InvoiceWorkflowPanel />
                            )}
                            {activeTab === 9 && (
                                <CustomerManagementPanel />
                            )}
                        </Box>

                        {/* Glass Sidebar */}
                        {isSidebarOpen && selectedItem && (
                            <Box sx={{
                                width: 420,
                                borderLeft: '1px solid',
                                borderColor: 'divider',
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(20px)',
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                animation: 'slideIn 0.3s ease-out',
                                overflowY: 'auto'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {selectedItem._type === 'invoice' ? <InvoicesIcon /> : <SurchargeIcon />}
                                        {selectedItem._type === 'invoice' ? t('common.financial.invoiceFulfillment', 'Invoice Fulfillment') : t('common.financial.ruleOrchestration', 'Rule Orchestration')}
                                    </Typography>
                                    <IconButton onClick={() => setIsSidebarOpen(false)} size="small">
                                        <MoreIcon sx={{ transform: 'rotate(90deg)' }} />
                                    </IconButton>
                                </Box>

                                <Box sx={{ mb: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                        <Avatar sx={{ width: 64, height: 64, fontSize: '1.5rem', bgcolor: theme.palette.secondary.main }}>
                                            {selectedItem._type === 'invoice' ? 'INV' : 'RL'}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h5">{selectedItem.invoice_number || selectedItem.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{t('common.financial.registryId', 'Registry ID')}: {selectedItem.id}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        <Chip
                                            label={selectedItem.status || (selectedItem.is_active ? t('common.active', 'Active') : t('common.inactive', 'Inactive'))}
                                            color={selectedItem.status === 'paid' || selectedItem.is_active ? 'success' : 'warning'}
                                            size="small"
                                        />
                                        <Chip label={selectedItem._type === 'invoice' ? t('common.financial.commercial', 'Commercial') : t('common.financial.strategic', 'Strategic')} variant="outlined" size="small" />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {selectedItem._type === 'invoice' ? (
                                        <>
                                            <Box>
                                                <Typography variant="overline" color="text.secondary">{t('common.financial.commercialDetails', 'Commercial Details')}</Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2">{t('common.customer', 'Customer')}</Typography>
                                                        <Typography variant="body2" fontWeight="bold">{selectedItem.customer_name || '-'}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2">{t('common.financial.amountDue', 'Amount Due')}</Typography>
                                                        <Typography variant="body2" fontWeight="bold">{formatCurrency(selectedItem.total_amount || 0, selectedItem.currency || 'EUR')}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2">{t('common.financial.dueDate', 'Due Date')}</Typography>
                                                        <Typography variant="body2" fontWeight="bold">{selectedItem.due_date ? formatDate(selectedItem.due_date, 'dd MMM yyyy') : '-'}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                                                                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                                            <Button fullWidth variant="contained" startIcon={<PdfIcon />} onClick={() => setPdfGeneratorOpen(true)}>
                                                                                                {t('billing.pdf.generator.open', 'PDF Generator')}
                                                                                            </Button>
                                                                                            <Button fullWidth variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleDownloadPDF(selectedItem.id)} disabled={downloadPDFMutation.isPending}>
                                                                                                {downloadPDFMutation.isPending ? t('common.downloading', 'Downloading...') : t('common.financial.downloadPdf', 'Quick Download PDF')}
                                                                                            </Button>
                                                                                            <Button fullWidth variant="outlined" startIcon={<EmailIcon />} onClick={() => setEmailDialogOpen(true)}>
                                                                                                {t('common.financial.emailToClient', 'Email to Client')}
                                                                                            </Button>
                                                                                        </Box>
                                        </>
                                    ) : (
                                        <>
                                            <Box>
                                                <Typography variant="overline" color="text.secondary">{t('common.financial.ruleConfiguration', 'Rule Configuration')}</Typography>
                                                <Box sx={{ p: 2, mt: 1, borderRadius: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                        <Typography variant="body2">{t('common.financial.ruleType', 'Rule Type')}</Typography>
                                                        <Typography variant="body2" fontWeight="bold">{selectedItem.type}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2">{t('common.financial.priorityLevel', 'Priority Level')}</Typography>
                                                        <Typography variant="body2" fontWeight="bold">{selectedItem.priority}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Button fullWidth variant="contained" startIcon={<TestIcon />}>
                                                    {t('common.financial.runLogicTest', 'Run Logic Test')}
                                                </Button>
                                                <Button fullWidth variant="outlined" startIcon={<EditIcon />}>
                                                    {t('common.financial.modifyParameters', 'Modify Parameters')}
                                                </Button>
                                            </Box>
                                        </>
                                    )}

                                    <Box>
                                        <Typography variant="overline" color="text.secondary">{t('common.financial.auditTrail', 'Audit Trail')}</Typography>
                                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                            {t('common.financial.lastModified', 'Last modified by Supervisor on 2026-01-03')}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Button fullWidth variant="text" color="error">
                                        {t('common.financial.archiveRecord', 'Archive Strategic Record')}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </GlassPanel>

            <style>
                {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
            </style>

            <InvoicePDFGenerator
                invoiceId={selectedItem?.id}
                open={pdfGeneratorOpen}
                onClose={() => setPdfGeneratorOpen(false)}
            />
        </PageWrapper>
    );
};

export default React.memo(FinancialCockpitPage);
